import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, MessageSquare, MapPin, Clock, CheckCircle2, CalendarCheck, Facebook, ShieldCheck } from "lucide-react";
import { MaskLine, Reveal } from "@/lib/motion";
import { SITE, SERVICES, SELECTABLE_PACKAGES } from "@/data/site";
import { BookingLink } from "@/components/BookingLink";
import { PhoneLink, TextLink } from "@/components/PhoneLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FIELD = "w-full bg-transparent border-b border-[#C9BFA8] focus:border-[#8a6b2f] outline-none py-3 text-navy placeholder:text-ink-muted/60 transition-colors focus-visible:ring-2 focus-visible:ring-gold/50 rounded-sm";
const LABEL = "font-mono text-[12px] uppercase tracking-[0.18em] text-ink";

export default function Contact() {
  const [params] = useSearchParams();
  // /contact?package=essentials|standard preselects that package. Unavailable tiers (Full Service) are ignored.
  const preselected = SELECTABLE_PACKAGES.find((p) => p.id === params.get("package"))?.label;
  const EMPTY_FORM = { full_name: "", email: "", company: "", package: preselected || "", services: [], message: "" };
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {emailSent: bool}

  useEffect(() => {
    if (!preselected) return;
    setForm((f) => ({ ...f, package: preselected }));
    // Bring the form into view so the preselected package is immediately visible.
    const t = setTimeout(() => document.getElementById("inquiry-form")?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
    return () => clearTimeout(t);
  }, [preselected]);

  const toggleService = (title) =>
    setForm((f) => ({ ...f, services: f.services.includes(title) ? f.services.filter((s) => s !== title) : [...f.services, title] }));

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete your name, email, and message.");
      return;
    }
    if (!EMAIL_RE.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      const emailSent = Boolean(data?.email_sent);
      setResult({ emailSent });
      toast.success(emailSent ? "Inquiry sent — we'll be in touch by email." : "Inquiry saved — we'll follow up by email.");
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="contact-hero" className="bg-cream pt-40 pb-28 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.5] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(#E2DACB 1px,transparent 1px)", backgroundSize: "100% 46px" }} />
      <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        {/* Left: details + booking */}
        <div className="lg:col-span-5">
          <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-8">Contact</p>
          <h1 className="font-serif font-medium tracking-tight leading-[1.04] text-navy text-5xl lg:text-6xl">
            <MaskLine delay={0.1}>Let's talk about</MaskLine>
            <MaskLine delay={0.24}>your <span className="italic text-[#9a7a33]">books.</span></MaskLine>
          </h1>
          <p className="anim-up mt-8 text-ink-muted text-lg leading-relaxed max-w-md" style={{ animationDelay: "0.5s" }}>
            Book a consultation to choose an available appointment through Google Calendar, or send
            an inquiry below and we'll follow up by email.
          </p>
          <p data-testid="contact-service-area" className="anim-up mt-4 text-[15px] text-navy max-w-md" style={{ animationDelay: "0.6s" }}>
            Based in Arizona and working remotely with small businesses nationwide — consultations are by phone or video.
          </p>

          <div className="mt-8 bg-navy text-parchment p-7 border border-gold/30">
            <div className="flex items-center gap-3">
              <CalendarCheck size={20} className="text-gold" />
              <p className="font-serif text-2xl">Book a Consultation</p>
            </div>
            <p className="text-parchment-muted text-sm mt-3 leading-relaxed">
              You'll choose an available time on our Google Calendar booking page.
            </p>
            <BookingLink placement="contact-card" testId="contact-consultation-btn" className="group inline-flex items-center gap-3 mt-5 min-h-[44px] bg-gold text-navy px-7 py-3 font-mono text-[13px] uppercase tracking-[0.14em] hover:bg-gold-hover transition-colors">
              Book a Consultation <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </BookingLink>
          </div>

          <div className="mt-10 space-y-6">
            <div className="flex items-start gap-4 border-b border-[#D9CFB8] pb-5">
              <Mail size={18} className="text-[#8a6b2f] shrink-0 mt-1" />
              <div>
                <p className={LABEL}>Email</p>
                <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="text-navy mt-1 block hover:text-[#8a6b2f] transition-colors underline-offset-2 hover:underline">{SITE.email}</a>
              </div>
            </div>
            <div className="flex items-start gap-4 border-b border-[#D9CFB8] pb-5">
              <Phone size={18} className="text-[#8a6b2f] shrink-0 mt-1" />
              <div>
                <p className={LABEL}>Phone · {SITE.phoneLabel}</p>
                <PhoneLink placement="contact" className="text-navy mt-1 block hover:text-[#8a6b2f] transition-colors underline-offset-2 hover:underline" />
                <TextLink placement="contact" className="group inline-flex items-center gap-2 mt-3 min-h-[44px] font-mono text-[13px] uppercase tracking-[0.14em] text-navy border border-navy/30 px-4 py-2 hover:border-navy hover:bg-navy/5 transition-colors">
                  <MessageSquare size={13} className="text-[#8a6b2f]" /> Text us
                </TextLink>
              </div>
            </div>
            <div className="flex items-start gap-4 border-b border-[#D9CFB8] pb-5">
              <MapPin size={18} className="text-[#8a6b2f] shrink-0 mt-1" />
              <div>
                <p className={LABEL}>{SITE.addressLabel}</p>
                {SITE.addressLines.map((l) => <p key={l} className="text-navy mt-1 leading-snug">{l}</p>)}
              </div>
            </div>
            <div className="flex items-start gap-4 border-b border-[#D9CFB8] pb-5">
              <Clock size={18} className="text-[#8a6b2f] shrink-0 mt-1" />
              <div>
                <p className={LABEL}>Business Hours</p>
                {SITE.hours.map((h) => <p key={h.d} className="text-navy mt-1">{h.d}: {h.t}</p>)}
                <p className="text-ink-muted mt-1 text-sm">{SITE.timezone}</p>
              </div>
            </div>
            <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" aria-label="Oakford Ledger on Facebook"
              data-testid="contact-facebook-link"
              className="inline-flex items-center gap-2 min-h-[44px] text-navy hover:text-[#8a6b2f] transition-colors">
              <Facebook size={18} /> <span className="font-mono text-[13px] uppercase tracking-[0.14em]">Facebook</span>
            </a>
          </div>
        </div>

        {/* Right: inquiry form */}
        <div className="lg:col-span-7">
          <Reveal>
            <div className="bg-cream-ivory border border-[#D9CFB8] p-8 lg:p-12">
              <p className="font-mono text-[12px] uppercase tracking-[0.24em] text-[#8a6b2f] mb-2">Send an Inquiry</p>
              {result ? (
                <div data-testid="inquiry-success" className="text-center py-16">
                  <CheckCircle2 size={48} className="text-[#8a6b2f] mx-auto" strokeWidth={1.4} />
                  <h2 className="font-serif text-3xl text-navy mt-6">Thank you.</h2>
                  <p className="text-ink-muted mt-3 max-w-sm mx-auto">
                    {result.emailSent
                      ? "Your inquiry has been sent and we'll follow up by email shortly."
                      : "Your inquiry has been saved. If you don't hear from us soon, please email us directly."}
                  </p>
                  <button onClick={() => setResult(null)} data-testid="send-another-btn"
                    className="mt-8 min-h-[44px] font-mono text-[13px] uppercase tracking-[0.14em] text-navy link-underline">
                    Send another inquiry
                  </button>
                </div>
              ) : (
                <form id="inquiry-form" onSubmit={submit} data-testid="contact-inquiry-form" className="space-y-8 scroll-mt-28" noValidate>
                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <label className={LABEL} htmlFor="full_name">Full name *</label>
                      <input id="full_name" required data-testid="input-full-name" className={FIELD} value={form.full_name} onChange={update("full_name")} placeholder="Jane Whitfield" />
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="email">Email *</label>
                      <input id="email" type="email" required data-testid="input-email" className={FIELD} value={form.email} onChange={update("email")} placeholder="jane@company.com" />
                    </div>
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="company">Business name (optional)</label>
                    <input id="company" data-testid="input-company-name" className={FIELD} value={form.company} onChange={update("company")} placeholder="Whitfield & Co." />
                  </div>

                  <fieldset data-testid="service-interest" className="space-y-7">
                    <legend className={LABEL}>Service interest</legend>

                    <div>
                      <p className="font-mono text-[12px] uppercase tracking-[0.16em] text-ink-muted mb-3">Monthly package · choose one</p>
                      <div role="radiogroup" aria-label="Monthly package" className="flex flex-wrap gap-2">
                        {[...SELECTABLE_PACKAGES.map((p) => ({ id: p.id, value: p.label, title: `${p.name} · ${p.price}/mo` })), { id: "unsure", value: "Not sure yet", title: "Not sure yet" }].map((opt) => {
                          const on = form.package === opt.value;
                          return (
                            <button key={opt.id} type="button" role="radio" aria-checked={on} data-testid={`package-option-${opt.id}`}
                              onClick={() => setForm((f) => ({ ...f, package: on ? "" : opt.value }))}
                              className={`min-h-[44px] font-mono text-[13px] uppercase tracking-[0.12em] px-4 py-2.5 border transition-colors ${on ? "bg-navy text-parchment border-navy" : "border-[#C9BFA8] text-navy hover:border-navy"}`}>
                              {opt.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="font-mono text-[12px] uppercase tracking-[0.16em] text-ink-muted mb-3">Services · click all that apply</p>
                      <div role="group" aria-label="Services" className="flex flex-wrap gap-2">
                        {SERVICES.map((s) => {
                          const on = form.services.includes(s.title);
                          return (
                            <button key={s.id} type="button" role="checkbox" aria-checked={on} data-testid={`service-option-${s.id}`} onClick={() => toggleService(s.title)}
                              className={`inline-flex items-center gap-2 min-h-[44px] font-mono text-[13px] uppercase tracking-[0.12em] px-4 py-2.5 border transition-colors ${on ? "bg-[#8a6b2f] text-cream border-[#8a6b2f]" : "border-[#C9BFA8] text-navy hover:border-[#8a6b2f]"}`}>
                              {on && <CheckCircle2 size={13} aria-hidden="true" />} {s.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </fieldset>
                  <div>
                    <label className={LABEL} htmlFor="message">How can we help? *</label>
                    <textarea id="message" required data-testid="input-message" rows={4} className={`${FIELD} resize-none`} value={form.message} onChange={update("message")} placeholder="Tell us about your business and what you're looking for..." />
                  </div>
                  <aside data-testid="contact-privacy-notice" role="note" className="border-l-2 border-[#C5A059] bg-cream-ivory/70 pl-5 pr-4 py-4">
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[#8a6b2f]">
                      <ShieldCheck size={14} className="shrink-0" aria-hidden="true" /> Protect your information
                    </p>
                    <p className="mt-2 text-sm text-ink leading-relaxed">
                      Please use this form for general inquiries only. Do not submit bank or credit card account numbers, passwords, Social Security numbers, bank statements, or other sensitive financial documents. If documents are needed, we will arrange an appropriate method for sharing them.
                    </p>
                  </aside>
                  <button type="submit" disabled={loading} data-testid="submit-inquiry-btn"
                    className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 min-h-[48px] bg-navy text-parchment px-8 py-3.5 font-mono text-[13px] uppercase tracking-[0.14em] border border-gold/30 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:translate-y-0">
                    {loading ? "Sending…" : "Submit Inquiry"}
                    {!loading && <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />}
                  </button>
                  <p data-testid="contact-privacy-policy-note" className="mt-6 text-[14px] text-ink-muted leading-relaxed">
                    Please review our <Link to="/privacy-policy" data-testid="contact-privacy-policy-link" className="text-navy link-underline">Privacy Policy</Link> for information about how we handle your personal information.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
